<script lang="ts" setup>
/**
 * @link https://idsk.gov.sk/komponenty/formularove-prvky#form-radio-buttons
 */
import { CLASS_ROOT, OrientationType } from './constants'
import { ref, watch } from 'vue'
import { v4 as uuid4 } from 'uuid'

export type IDSKRadiosOptionType = {
  id?: string
  value: string
  label: string
  dataTestId?: string
}

export interface IDSKRadiosProps {
  id?: string
  name?: string
  label?: string
  hint?: string
  hintId?: string
  /** Error message text, also serves as a flag whether the input is valid. */
  errorMessage?: string
  errorMessageId?: string
  options: ReadonlyArray<IDSKRadiosOptionType>
  /**
   * Render radio inputs side by side or stacked on top of each other.
   * For 3 or more input it's recommended to use the `vertical` orientation.
   */
  orientation?: OrientationType
  defaultValue?: string
  modelValue: string
  radiosWrapperClass?: string
}

const props = withDefaults(defineProps<IDSKRadiosProps>(), {
  id: undefined,
  label: undefined,
  name: undefined,
  hint: undefined,
  hintId: undefined,
  errorMessage: undefined,
  errorMessageId: undefined,
  orientation: 'vertical',
  defaultValue: undefined,
  modelValue: undefined,
  radiosWrapperClass: 'mb-6',
})

const state = ref<string | undefined>(props.modelValue || props.defaultValue)

defineEmits(['update:modelValue'])

watch(props, () => {
  if (props.modelValue) {
    state.value = props.modelValue
  }
})
</script>

<template>
  <div
    :class="[
      'govuk-form-group',
      {
        ['govuk-form-group--error']: errorMessage,
      },
    ]"
  >
    <fieldset class="govuk-fieldset">
      <IDSKFieldCaption
        :label="label"
        :hint="hint"
        :hint-id="hintId"
        :error-message="errorMessage"
        :error-message-id="errorMessageId"
        variant="legend"
      />
      <div
        :class="[
          CLASS_ROOT,
          {
            [`${CLASS_ROOT}--inline`]: props.orientation === 'horizontal',
          },
          props.radiosWrapperClass,
        ]"
      >
        <IDSKRadio
          v-for="option in props.options"
          v-bind="$attrs"
          :id="`${props.id}-${uuid4()}`"
          :key="option"
          v-model="state"
          :label="option.label"
          :value="option.value"
          :name="props.name"
          :data-test-id="
            option.dataTestId ? option.dataTestId : props.id + '-' + option.id
          "
          @input="(val) => $emit('update:modelValue', val?.target?.value)"
        >
          <template #default>{{ option.label }}</template>
          <template v-if="$slots[`${option.id}-visible-content`]" #visible-content>
            <slot :name="`${option.id}-visible-content`" />
          </template>
          <template v-if="$slots[`${option.id}-hidden-content`]" #hidden-content>
            <slot :name="`${option.id}-hidden-content`" />
          </template>
        </IDSKRadio>
      </div>
    </fieldset>
  </div>
</template>

<style scoped lang="scss"></style>
